import { useUpdateOnboardingUser } from "@features/onboarding/mutations/useUpdateOnboardingUser";
import { GenderEnumSchema } from "@fitties/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import logger from "@logger";
import DesiredGendersField from "@shared/components/DesiredGendersField";
import { useCurrentUser } from "@shared/queries";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Validation schema for desired gender selection
const desiredGenderSchema = z.object({
  desired_genders: z.array(GenderEnumSchema).min(1, "Please select at least one option"),
});

type DesiredGenderFormData = z.infer<typeof desiredGenderSchema>;

/**
 * Desired genders selection form component for onboarding flow.
 *
 * Displays a purple background with heading, a toggle switch for "Everyone" that selects
 * all gender options, and individual checkbox options for Women, Men, and Non-binary.
 * Uses React Hook Form and Zod for validation with proper form submission handling.
 *
 * @example
 * ```tsx
 * <DesiredGendersForm />
 * ```
 */
interface DesiredGendersFormProps {
  onSaved?: () => void;
}

export default function DesiredGendersForm({ onSaved }: DesiredGendersFormProps) {
  const { user } = useCurrentUser();
  const { mutate: updateOnboardingUser, isPending: isSubmitting, error: mutationError } = useUpdateOnboardingUser();

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DesiredGenderFormData>({
    resolver: zodResolver(desiredGenderSchema),
    defaultValues: {
      desired_genders: user?.desired_genders ?? [],
    },
    mode: "onChange",
  });

  const handleFormSubmit = (data: DesiredGenderFormData) => {
    logger.info("📝 DesiredGenderForm submitting data:", data);
    updateOnboardingUser(data, {
      onSuccess: (result) => {
        logger.info("✅ DesiredGenderForm mutation successful:", result);
        onSaved?.();
      },
      onError: (error) => {
        logger.error("❌ DesiredGenderForm mutation failed:", error);
      },
    });
  };

  return (
    <div
      data-testid="desired-genders-form-root"
      className="h-full min-h-0 flex flex-col text-center font-mono bg-lavender-400"
    >
      {/* Content */}
      <div data-testid="desired-genders-form-content" className="flex-1 overflow-y-auto space-y-4 pb-2">
        {/* Title */}
        <h1 data-testid="desired-genders-form-heading" className="text-2xl font-bold text-black">
          Who are you interested in?
        </h1>

        {/* Description */}
        <p data-testid="desired-genders-form-description" className="text-black/80 text-md">
          You can choose more than one option
        </p>

        {/* Form */}
        <form
          data-testid="desired-genders-form"
          onSubmit={handleSubmit(handleFormSubmit)}
          className="mx-auto w-full space-y-4"
          noValidate
        >
          {/* Server Error Message */}
          {mutationError && (
            <div data-testid="desired-genders-form-error" className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p data-testid="desired-genders-form-error-message" className="text-red-600 text-sm">
                {mutationError.message}
              </p>
            </div>
          )}

          <div data-testid="desired-genders-form-field-section">
            <DesiredGendersField
              value={watch("desired_genders") ?? []}
              onChange={(v) => setValue("desired_genders", v)}
              {...(errors.desired_genders?.message != null && {
                error: errors.desired_genders.message,
              })}
            />
          </div>
        </form>
      </div>

      {/* Button at bottom */}
      <div data-testid="desired-genders-form-footer" className="sticky bottom-0 z-10 bg-lavender-400 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(handleFormSubmit)}
          className={clsx("w-full px-8 py-4 text-lg rounded-lg font-medium transition-colors font-sans", {
            "bg-pink-600 text-white hover:bg-pink-700": !isSubmitting,
            "bg-gray-300 text-gray-500 cursor-not-allowed": isSubmitting,
          })}
          data-testid="submit-button"
        >
          <span data-testid="desired-genders-form-submit-label">{isSubmitting ? "Saving..." : "Continue"}</span>
        </button>
      </div>
    </div>
  );
}
